'use client';

import Link from 'next/link';
import { forceDownload, getSafeFilename } from '@/app/lib/pdf-utils';
import { HubItem } from '@workspace/interfaces';
import { AppImage, Icon, Typography } from '@workspace/ui';
import { handleLinkClick } from '@workspace/hooks';

interface DocumentHubItemProps {
  item: HubItem;
  viewCTA?: string;
  downloadCTA?: string;
}

export function DocumentHubItem({ item, downloadCTA, viewCTA }: DocumentHubItemProps) {
  const { title, filename, fileUrl = '', image } = item;

  const onDownload = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (!fileUrl) return;

    await forceDownload(fileUrl, getSafeFilename(filename || undefined));
  };

  return (
    <div className="md:p-4 py-4 pl-3 pr-2 leading-5 rounded-md border border-secondary-100 bg-white xl:h-31 xl:w-96.75">
      <div className="flex justify-between md:gap-4 gap-3">
        <div
          className="relative w-16 h-[90px] shrink-0 overflow-hidden flex items-center justify-center bg-secondary-50"
          aria-hidden
        >
          {image ? (
            <AppImage
              src={image}
              alt={title ? `${title} thumbnail` : 'Document thumbnail'}
              className="object-cover"
              fill
              sizes="64px"
            />
          ) : (
            <Icon
              name="FileBend"
              size={32}
              color="var(--color-raisin-black)"
              className="pointer-events-none select-none"
            />
          )}
        </div>

        <div className="flex flex-col gap-auto flex-1 min-w-0 justify-between md:pl-3 pl-2">
          {title ? (
            <Typography variant="bodySm" className={'font-bold line-clamp-2 text-ellipsis'}>
              {title}
            </Typography>
          ) : null}

          <div className="flex items-center gap-8">
            {fileUrl ? (
              <>
                <Link
                  href={fileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  prefetch={false}
                  className={'underline text-base leading-tight font-semibold text-sans text-primary'}
                  onClick={(e) =>
                    handleLinkClick(
                      {
                        type: 'external',
                        url: fileUrl,
                        label: viewCTA ?? 'View',
                      },
                      e,
                    )
                  }
                >
                  {viewCTA ?? 'View'}
                </Link>

                <Link
                  href={fileUrl}
                  prefetch={false}
                  className={'underline text-base leading-tight font-semibold text-sans text-primary'}
                  onClick={onDownload}
                >
                  {downloadCTA ?? 'Download'}
                </Link>
              </>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
